/**
 * Eine Zeile der Aufgabenliste — geteilt zwischen dem Reiter *Aufgaben* eines
 * Objekts und dem Bereich *Aufgaben* (Spec 06a, „Was neu entsteht": „Bereich
 * und Reiter teilen sich eine Listenkomponente", dieselbe Aufteilung wie
 * `ContractList`/`ContractRow`).
 *
 * Ein Klick öffnet `task-sheet` zum Ändern (`onOpen`) — anders als
 * `ContractRow` navigiert die Zeile **nicht**: Eine Aufgabe hat keinen
 * eigenen Bildschirm, `TREE_KINDS` kennt `kind='ticket'` nicht (Spec 06a,
 * AK-387), sie wird ausschließlich über das Sheet geändert.
 *
 * Die Priorität bekommt zusätzlich zum Text (`priority.label`, wie iOS es
 * bereits zeigt) einen farbigen Punkt (Spec 06a, „Schmale Ansicht"). `accent`
 * und `status-overdue` sind reserviert (Akzent: primäre Aktion/aktiver
 * Zustand; Statusfarben: nur Status — `design-system`-Skill), und `border`
 * ist auf `--surface` praktisch unsichtbar (Befund W2,
 * `reports/15-design-reviewer.md`) — der Punkt nutzt deshalb nur zwei
 * neutrale, tatsächlich sichtbare Töne: `muted` für niedrig/normal, `text`
 * für hoch/dringend. Er bleibt rein dekorativ (`aria-hidden`); die genaue
 * Stufe steht als Text daneben, Farbe ist nie das einzige Signal.
 *
 * Unter 768 px stapelt die Zeile Pfad, Fälligkeit, Person, Status und Kosten
 * zu **einer** umbrechenden Zeile mit „·"-Trennern (Spec „Schmale Ansicht"),
 * dieselbe Form wie `TaskRowView.swift` auf iOS. Der Gruppen-Wrapper trägt
 * dafür `md:contents`: Ab 768 px erzeugt er selbst keine Box mehr, seine
 * Kinder werden wieder zu eigenen Spalten mit fester Breite — genau wie
 * vorher, nur ohne eine zweite Fassung der Feldliste zu pflegen.
 */

import { assigneeLabel, subjectLabel } from '@/features/tasks/labels'
import type { TicketRow } from '@/features/tasks/api'
import { formatDate } from '@/lib/date'

const PRIORITY_DOT_CLASS: Record<string, string> = {
  low: 'bg-muted',
  normal: 'bg-muted',
  high: 'bg-text',
  urgent: 'bg-text',
}

interface TaskRowProps {
  readonly ticket: TicketRow
  readonly onOpen: () => void
  readonly onDone: () => void
}

export function TaskRow({ ticket, onOpen, onDone }: TaskRowProps) {
  const assignee = assigneeLabel(ticket.assignee)
  // `closed_at` kommt vom Server (I9) — kein zweiter, im Client
  // nachgerechneter Statussatz (Befund W-B3, `reports/13-gatekeeper.md`);
  // iOS prüft an derselben Stelle bereits genauso (`TaskRowView.swift:78`).
  const canClose = ticket.closed_at === null

  return (
    <li
      data-testid={`task-row-${ticket.id}`}
      className="border-border flex min-h-(--row-h) flex-col gap-1 border-b px-(--page-x) py-2 last:border-b-0 md:h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-0"
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left md:flex-row md:items-center md:gap-3"
      >
        {/* Titel — die Hauptaussage der Zeile, deshalb `flex-1` statt einer
         * festen Breite (Befund W3, `reports/15-design-reviewer.md`: bei
         * 1440 px kürzte `md:w-48` den Titel auf 192 px, während der Pfad
         * ~570 px bekam — vertauscht mit dem Feldblock unten). */}
        <span className="flex min-w-0 items-center gap-2 md:flex-1">
          <span
            aria-hidden="true"
            className={`size-2 shrink-0 rounded-full ${PRIORITY_DOT_CLASS[ticket.priority.value] ?? 'bg-muted'}`}
          />
          <span className="text-body text-text truncate font-medium">{ticket.title}</span>
          <span className="text-label text-muted shrink-0">{ticket.priority.label}</span>
          {ticket.is_overdue && (
            <span
              data-testid={`task-row-overdue-${ticket.id}`}
              className="text-status-overdue text-label shrink-0 font-medium"
            >
              Überfällig
            </span>
          )}
        </span>

        <span className="text-label text-muted flex flex-wrap items-center gap-x-1 gap-y-0.5 md:contents">
          <span data-testid={`task-row-path-${ticket.id}`} className="truncate md:w-48 md:shrink-0">
            {subjectLabel(ticket.subject)}
          </span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          <span className="shrink-0 md:w-24">{ticket.due_on === null ? '—' : formatDate(ticket.due_on)}</span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          <span className="shrink-0 truncate md:w-32">{assignee ?? '—'}</span>
          <span aria-hidden="true" className="md:hidden">
            ·
          </span>
          <span className="border-border shrink-0 rounded-card border px-2 py-0.5">{ticket.status.label}</span>
          {ticket.cost_display !== null && (
            <>
              <span aria-hidden="true" className="md:hidden">
                ·
              </span>
              {/* `cost_display` kommt vom Server (Befund W7,
               * `reports/15-design-reviewer.md`) — kein Client formatiert
               * Cent-Beträge selbst, dieselbe Regel wie bei `Charge`/`Asset`. */}
              <span data-numeric className="shrink-0 md:w-20">
                {ticket.cost_display}
              </span>
            </>
          )}
        </span>
      </button>

      {canClose && (
        <button
          type="button"
          data-testid={`task-done-${ticket.id}`}
          onClick={(event) => {
            event.stopPropagation()
            onDone()
          }}
          className="border-border text-text hover:bg-bg rounded-card text-label shrink-0 border px-2 py-1 transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          Erledigt
        </button>
      )}
    </li>
  )
}
